import {
  Box,
  ContentBlock,
  Dialog,
  Heading,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import React from 'react';

import {
  formatEventTileDate,
  renderDescriptionParagraphs,
  splitEventsByDate,
} from '#shared/eventUtils';
import type { PublishedEvent } from '#shared/types';
import { fetchEvents } from '#src/api/events';
import { EventBlock } from '#src/components/EventBlock/EventBlock';
import { PageWrapper } from '#src/components/PageWrapper/PageWrapper';
import { WhatsNextBanner } from '#src/components/WhatsNextBanner/WhatsNextBanner';

export const Home = () => {
  const [events, setEvents] = React.useState<PublishedEvent[]>([]);
  const [activeEvent, setActiveEvent] = React.useState<PublishedEvent | null>(
    null,
  );
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchEvents()
      .then((snapshot) => setEvents(snapshot.events))
      .catch(() => setError('Unable to load events'))
      .finally(() => setLoading(false));
  }, []);

  const { upcoming, past, highlighted } = splitEventsByDate(events);

  return (
    <PageWrapper>
      <ContentBlock>
        <Box
          paddingX={{ mobile: 'small', tablet: 'none' }}
          paddingBottom="large"
        >
          {loading ? <Text>Loading events…</Text> : null}
          {error ? <Text tone="critical">{error}</Text> : null}

          {!loading && !error ? (
            <Stack space="xlarge">
              <Tiles space="small" columns={{ mobile: 1, tablet: 3 }}>
                {upcoming.map((event) => (
                  <EventBlock
                    key={event.id}
                    name={event.title}
                    date={formatEventTileDate(event)}
                    image={event.imageUrl}
                    onClick={() => setActiveEvent(event)}
                  />
                ))}
              </Tiles>

              {past.length > 0 ? (
                <Stack space="medium">
                  <Heading level="2">Past Events</Heading>
                  <Tiles space="small" columns={{ mobile: 1, tablet: 3 }}>
                    {past.map((event) => (
                      <EventBlock
                        key={event.id}
                        name={event.title}
                        date={formatEventTileDate(event)}
                        image={event.imageUrl}
                        onClick={() => setActiveEvent(event)}
                      />
                    ))}
                  </Tiles>
                </Stack>
              ) : null}
            </Stack>
          ) : null}
        </Box>
      </ContentBlock>

      {highlighted ? (
        <Box paddingBottom="large">
          <WhatsNextBanner
            title={highlighted.title}
            date={formatEventTileDate(highlighted)}
            image={highlighted.imageUrl ?? ''}
            onClick={() => setActiveEvent(highlighted)}
          />
        </Box>
      ) : null}

      <Dialog
        id="event-details"
        title={activeEvent?.title ?? ''}
        open={activeEvent !== null}
        onClose={() => setActiveEvent(null)}
        width="large"
      >
        {activeEvent ? (
          <Stack space="medium">
            <Text tone="secondary">{formatEventTileDate(activeEvent)}</Text>
            {renderDescriptionParagraphs(activeEvent.description).map(
              (paragraph) => (
                <Text key={paragraph.slice(0, 40)}>{paragraph}</Text>
              ),
            )}
          </Stack>
        ) : null}
      </Dialog>
    </PageWrapper>
  );
};
